import { MappingDimensionKey } from '@pubstack/common/src/bid-params-mapping'
import { productOfBidderSeatType, roundBidAdjustment } from '@pubstack/common/src/bidder-seat'
import { useState } from 'react'
import { useCreateBidderSeat } from '~/api/bidder-seats.api'
import { useToast } from '~/components/Toast/useToasts'
import { Product } from '~/hooks/useProduct'
import { defaultSeatMappingType, MAPPING_DIMENSIONS_BY_TYPE, SEAT_SPEC_BY_METHOD, SeatMappingType, SeatSetupMethod } from './seatSetupOption'

type SubmitValues = {
  name: string
  bidAdjustment: { enable: boolean; adjustment: number }
  /** Ignored by the placement-mapped flows, which are always keyed by placement. */
  mappingType?: SeatMappingType
  declaredPlacements?: { placementId: string; bidParams: Record<string, string> }[]
}

type Args = {
  scopeId: string
  /** Receives the product the seat landed in, which the chosen type decides — not necessarily the one the page is on. */
  onSeatCreated: (name: string, product: Product) => void
  onClose: () => void
  onNameError?: () => void
}

export const useCreateBidderSeatSubmit = ({ scopeId, onSeatCreated, onClose, onNameError }: Args) => {
  const [nameError, setNameError] = useState('')
  // The chosen method decides the product: both mutations are instantiated, one is picked at submit.
  // The endpoints are not interchangeable — ADM registers a bidder alias, PBS updates the auto-add
  // bidder rules and stack outputs — and the server rejects a seat type of the other product.
  const { mutateAsync: createAdmBidderSeat, isPending: isAdmPending } = useCreateBidderSeat('adm')
  const { mutateAsync: createPbsBidderSeat, isPending: isPbsPending } = useCreateBidderSeat('pbs')
  const toast = useToast()

  const submit = async (method: SeatSetupMethod, bidderCode: string, values: SubmitValues) => {
    if (!scopeId) return

    const { type, platform } = SEAT_SPEC_BY_METHOD[method]
    const product = productOfBidderSeatType(type)
    const isSpark = method === 'spark-for-pubs'
    // Keyed on the seat type rather than the method, so the internal flyout and the shared stepper cannot
    // drift into producing two different-looking Spark Demand seats.
    const isSparkDemand = type === 'spark_demand'
    const adjustmentEnabled = !isSpark && values.bidAdjustment.enable
    const isPlacementMapped = isSparkDemand || isSpark
    const mappingDimensions: MappingDimensionKey[] = isPlacementMapped ? ['placementId'] : MAPPING_DIMENSIONS_BY_TYPE[values.mappingType ?? defaultSeatMappingType(method)]
    const createBidderSeat = product === 'adm' ? createAdmBidderSeat : createPbsBidderSeat

    try {
      await createBidderSeat({
        name: values.name,
        bidderCode,
        bidAdjustment: adjustmentEnabled ? { enabled: true, value: roundBidAdjustment(values.bidAdjustment.adjustment / 100) } : { enabled: false },
        mappingDimensions,
        type,
        platform,
        ...(isSparkDemand ? { isInternal: true } : {}),
        // Only the spark flow declares placements: never send rows filled before a switch to another method.
        ...(isSpark && values.declaredPlacements
          ? {
              declaredPlacements: values.declaredPlacements
                .filter((row) => row.placementId)
                .map((row) => ({ placementId: row.placementId, bidParams: Object.fromEntries(Object.entries(row.bidParams).filter(([, value]) => value.trim() !== '')) })),
            }
          : {}),
      })
      onSeatCreated(values.name, product)
      onClose()
    } catch (error) {
      // `useMutation` rejects with the response body itself, not an AxiosError — there is no
      // `error.response`. The reason lives in `description`; `message` is only the status label.
      const description = error?.description
      if (description?.field === 'name') {
        setNameError(description.message)
        onNameError?.()
      } else {
        setNameError('')
        toast.alert(description?.message ?? error?.message ?? 'Failed to create seat')
      }
    }
  }

  return { submit, isPending: isAdmPending || isPbsPending, nameError, clearNameError: () => setNameError('') }
}
